/**
 * Kuwaiti Arabic.
 *
 * The line held throughout, because it is the decision that makes the difference
 * between local voice and a translation:
 *
 *  - SENTENCES are Kuwaiti. Empty states, helper text and descriptions say what a
 *    Kuwaiti says — ما في, اللي, عشان, مو, للحين, هني, مرة ثانية, قاعد يتسوّى,
 *    الشهر الجاي. Not وايد-for-its-own-sake, but the natural connectives a MSA
 *    translation would replace and instantly sound foreign doing it.
 *
 *  - BUTTONS AND PLACEHOLDERS are verbal nouns: إرسال الطلب, تحميل الكل, عرض الكل.
 *    Arabic interfaces do this because an imperative has to pick a gender, and an
 *    interface is read by a whole team. It is also simply the convention every
 *    Arabic product follows, so it reads as normal rather than as stiff.
 *
 *  - MONEY AND CONTRACT TERMS stay standard: رصيد, كشف الحساب, المخصص, المرحّل.
 *    A Kuwaiti finance team uses these words; a colloquial substitute would read as
 *    a mistake. "كريدت" is kept for the unit itself because that is what the team
 *    actually says for a borrowed product term, and the skill allows code-switching
 *    for exactly that.
 *
 * src/i18n.test.ts enforces the skill's banned-MSA list against this file, so
 * كيف / ماذا / الآن / غداً / أين / لماذا cannot slip back in.
 */
export const ar = {
  // navigation
  "nav.overview": "نظرة عامة",
  "nav.requests": "الطلبات",
  "nav.work": "الشغل",
  "nav.search": "البحث",
  "nav.catalogue": "قائمة الخدمات",
  "nav.catalogueEyebrow": "قائمة أسعار العقد المتفق عليها",
  "nav.statement": "كشف الحساب الشهري",
  "nav.ledger": "سجل الرصيد",

  // shell
  "shell.available": "المتوفر",
  "shell.requestService": "طلب خدمة",
  "shell.reportingMonth": "شهر التقرير",
  "shell.signOut": "تسجيل خروج",
  "shell.openNav": "فتح القائمة",
  "shell.closeNav": "سكّر القائمة",
  "shell.theme": "مظهر الألوان",
  "shell.themeLight": "مظهر فاتح",
  "shell.themeDark": "مظهر غامق",
  "shell.themeSystem": "حسب الجهاز",
  "shell.language": "اللغة",

  // overview
  "overview.title": "وضع رصيد الإنتاج",
  "overview.staffTitle": "نظرة سريعة على الحساب",
  "overview.intro": "الشغل المعتمد ينخصم من رصيد العقد حسب قائمة الأسعار المتفق عليها. والرصيد اللي ما ينصرف ينتقل للشهر الجاي تلقائي.",
  "overview.summary": "ملخص الرصيد",
  "overview.availableCredits": "الرصيد المتوفر",
  "overview.readyToUse": "جاهز للاستخدام",
  "overview.creditsSpent": "الرصيد المصروف",
  "overview.completedWork": "شغل مخلّص",
  "overview.creditsReserved": "الرصيد المحجوز",
  "overview.inProgressWork": "شغل معتمد وقاعد يتسوّى",
  "overview.totalPool": "إجمالي رصيد الشهر",
  "overview.creditPosition": "وضع الرصيد",
  "overview.readyHeading": "{n} كريدت جاهز للاستخدام",
  "overview.monthlyAllocation": "المخصص الشهري",
  "overview.carriedForward": "المرحّل من الشهر اللي فات",
  "overview.additionalCredits": "كريدت إضافي",
  "overview.whereWent": "وين راح الرصيد",
  "overview.byCategory": "المصروف حسب نوع الخدمة",
  "overview.latestActivity": "آخر الحركة",
  "overview.viewAll": "عرض الكل",

  // pace and forecast
  "pace.eyebrow": "المعدل",
  "pace.title": "ماشي على الخطة؟",
  "pace.thisVsLast": "هالشهر مقابل الشهر اللي فات",
  "pace.usualPace": "المعدل المعتاد",
  "pace.creditsPerMonth": "كريدت / الشهر",
  "pace.noComparison": "ما في مصروف الشهر اللي فات عشان نقارن.",
  "pace.notEnoughHistory": "ما في تاريخ كافي",
  "pace.paceNeedsMonth": "المعدل يحتاج شهر مخلّص وفيه شغل على الأقل.",
  "pace.noForecast": "ما في توقّع للحين",
  "pace.likelyUnused": "غالباً بيبقى",
  "pace.onPaceToUseAll": "المعدل يوصل لاستهلاك الرصيد كله",
  "pace.buysExample": "بـ {n} كريدت متوفر، مثلاً",
  "pace.estimateNotCommitment": "هذا تقدير، مو التزام.",
  "pace.singleMonth": "مبني على شهر واحد بس للحين — توقّعوا الرقم يتغيّر.",

  // requests
  "requests.title": "طلبات الخدمات",
  "requests.eyebrow": "الطلبات والتسليم",
  "requests.workflow": "مسار الإنتاج",
  "requests.workflowSub": "تابعوا كل طلب من البريف للتسليم",
  "requests.new": "طلب جديد",
  "requests.filters": "فلاتر حالة الطلب",
  "requests.all": "الكل",
  "requests.colRequest": "الطلب",
  "requests.colRequestedBy": "طالب الخدمة",
  "requests.colSource": "المصدر",
  "requests.colCredits": "الكريدت",
  "requests.colDue": "التاريخ المطلوب",
  "requests.colStatus": "الحالة",
  "requests.colView": "عرض",
  "requests.details": "تفاصيل الطلب",
  "requests.again": "طلبه مرة ثانية",
  "requests.openDelivery": "فتح التسليم",
  "requests.empty": "ما في طلبات للحين",
  "requests.emptyHint": "أي شي تطلبونه من بريزمك بيطلع هني.",

  // statuses
  "status.pending": "بانتظار المراجعة",
  "status.approved": "معتمد",
  "status.in_progress": "قاعد يتسوّى",
  "status.completed": "مخلّص",
  "status.rejected": "مرفوض",
  "status.canceled": "ملغي",

  // the work
  "work.title": "الشغل",
  "work.eyebrow": "كل اللي سلّمته بريزمك",
  "work.downloadAll": "تحميل الكل",
  "work.emptyTitle": "ما في شي متسلّم في {month}",
  "work.emptyHint": "الشغل المخلّص يطلع هني أول ما بريزمك تربطه بالطلب.",

  // request form
  "form.chooseService": "اختيار الخدمة",
  "form.addBrief": "كتابة البريف",
  "form.whatToCreate": "شنو تبون نسوي لكم؟",
  "form.chooseHint": "اختاروا خدمة عشان يفتح لكم البريف الجاهز.",
  "form.searchServices": "البحث بين {n} خدمة",
  "form.allServices": "كل الخدمات",
  "form.mostRequested": "الأكثر طلباً",
  "form.mostRequestedSub": "أسرع طريقة تبدون فيها طلب متكرر",
  "form.moreServices": "خدمات ثانية",
  "form.browseAll": "عرض باقي {n} خدمة",
  "form.noServices": "ما لقينا خدمات",
  "form.noServicesHint": "جرّبوا كلمة ثانية أو اختاروا تصنيف ثاني.",
  "form.changeService": "تغيير الخدمة",
  "form.essentials": "الأساسيات",
  "form.successLooksLike": "نبي نعرف شنو النجاح بالنسبة لكم",
  "form.quantity": "العدد",
  "form.requestTitle": "عنوان الطلب",
  "form.brief": "البريف",
  "form.briefPlaceholder": "الهدف، الرسالة الأساسية، الجمهور، المقاسات، وأي تفاصيل ضرورية",
  "form.dueDate": "التاريخ المطلوب",
  "form.channel": "القناة",
  "form.channelPlaceholder": "إنستغرام، إكس، داخلي",
  "form.language": "اللغة",
  "form.referenceLink": "رابط مرجعي",
  "form.optional": "اختياري",
  "form.referencePlaceholder": "رابط موود بورد أو مجلد أو مثال",
  "form.estimated": "الخصم التقديري",
  "form.included": "مشمول ضمن ساعات العمل",
  "form.submit": "إرسال الطلب",
  "form.submitting": "قاعد يرسل…",
  "form.saveAsBrief": "حفظ كبريف",
  "form.saving": "قاعد يحفظ…",
  "form.cancel": "إلغاء",
  "form.newRequest": "طلب جديد",
  "form.sameAgain": "نفس الطلب مرة ثانية",
  "form.copiedTitle": "منسوخ من طلب سابق",
  "form.fromTemplateTitle": "بادي من بريف محفوظ",
  "form.copiedHint": "البريف والعدد جاهزين. حطّوا تاريخ جديد، والكريدت المعروض بسعر اليوم.",

  // saved briefs
  "briefs.eyebrow": "البريفات المحفوظة",
  "briefs.title": "ابدوا من وحدة كاتبينها من قبل",
  "briefs.retired": "{service} ما عادت متوفرة",

  // search
  "search.title": "البحث",
  "search.eyebrow": "الطلبات والسجل، طول فترة العقد",
  "search.placeholder": "البحث في الطلبات والسجل — عنوان، بريف، خدمة",
  "search.scope": "كل شي في الحساب، طول فترة العقد.",
  "search.tooShort": "لازم حرفين على الأقل.",
  "search.failed": "ما قدرنا نسوي البحث. جرّبوا مرة ثانية.",
  "search.searching": "قاعد يدوّر…",
  "search.nothing": "ما في نتائج لـ ”{q}“",
  "search.nothingHint": "جرّبوا كلمة أقصر، أو جزء من اسم الخدمة.",
  "search.requests": "الطلبات",
  "search.requestsSub": "الشغل اللي طلبتوه",
  "search.ledger": "السجل",
  "search.ledgerSub": "الداخل والخارج من الرصيد",
  "search.truncated": "نعرض آخر 25. ضيّقوا البحث عشان تشوفون الباقي.",

  // statement and ledger
  "statement.title": "كشف الحساب الشهري",
  "statement.eyebrow": "الداخل، المصروف، والباقي",
  "statement.heading": "كشف حساب رصيد الإنتاج",
  "statement.print": "طباعة",
  "statement.exportCsv": "تصدير CSV",
  "statement.downloadPdf": "تحميل PDF",
  "ledger.title": "سجل الرصيد",
  "ledger.eyebrow": "سجل الحركات الشهري",
  "ledger.colService": "الخدمة",
  "ledger.accountCredits": "كريدت الحساب",

  // shared
  "common.credits": "كريدت",
  "common.month": "الشهر",
  "common.notSet": "غير محدد",
  "common.loading": "قاعد يحمّل",
  "pace.deltaVs": "مقابل {month}",
  "pace.averagedOver": "متوسط على {n} شهر مخلّص. هالشهر لسّه ماشي وما ينحسب.",
  "pace.atRateAcross": "على {rate} بالشهر عبر {n} شهر باقي.",
  "pace.byMonth": "بحلول {month}",
  "pace.monthsLeft": "باقي {n} شهر على العقد.",
  "pace.monthsLeft_one": "باقي شهر واحد على العقد.",
  "pace.nothingSpare": "على {rate} بالشهر ما بيبقى شي زايد. رتّبوا شغل إضافي بدري.",
  "meter.spent": "مصروف",
  "meter.reserved": "محجوز",
  "meter.available": "متوفر",
  "meter.label": "{available} كريدت متوفر من {total}",
  "overview.poolBreakdown": "{base} أساسي + {added} مضاف",
  "common.crEach": "{n} كريدت للوحدة",
  "common.cr": "كريدت",
  // service catalogue
  "catalogue.eyebrow": "قائمة أسعار واحدة متفق عليها",
  "catalogue.title": "اختاروا أي خدمة من بريزمك",
  "catalogue.intro": "الكريدت الشهري والكريدت الإضافي المشترى يمشون على نفس قائمة أسعار العقد. الخدمات المشمولة ما تخصم كريدت.",
  "catalogue.monthly": "{n} كريدت بالشهر",
  "catalogue.includedDeduct": "الخدمات المشمولة ما تخصم شي",
  "catalogue.search": "البحث في الخدمات",
  "catalogue.category": "فئة الخدمة",
  "catalogue.allCategories": "كل الفئات",
  "catalogue.count": "{n} خدمة",
  "catalogue.count_one": "خدمة وحدة",
  "catalogue.colService": "الخدمة",
  "catalogue.colCategory": "الفئة",
  "catalogue.colUnit": "الوحدة",
  "catalogue.colCredits": "الكريدت",
  "catalogue.colAction": "إجراء",
  "catalogue.included": "مشمولة",
  "catalogue.request": "طلب",
  "catalogue.requestThis": "طلب {service}",
  "catalogue.emptyTitle": "ما في خدمات مطابقة",
  "catalogue.emptyBody": "جرّبوا كلمة ثانية أو فئة ثانية.",

  // the service picker on the request form — the catalogue being chosen from
  "picker.eyebrow": "اختيار بضغطة وحدة",
  "picker.title": "شنو تبون نسوّي لكم؟",
  "picker.intro": "اختاروا خدمة عشان يفتح البريف الجاهز حقها.",
  "picker.search": "البحث في {n} خدمة",
  "picker.search_one": "البحث في خدمة وحدة",
  "picker.searchLabel": "البحث في خدمات الطلب",
  "picker.categories": "فئات الخدمات",
  "picker.all": "كل الخدمات",
  "picker.browseAll": "تصفّح باقي {n} خدمة",
  "picker.browseAll_one": "تصفّح الخدمة الثانية",
  "picker.more": "خدمات إضافية",
  "picker.results": "نتائج البحث",
  "picker.featured": "الأكثر طلباً",
  "picker.featuredSub": "أسرع طريقة تبدون فيها طلب متكرر",
  "picker.optionsAvailable": "{n} خيار متوفر",
  "picker.optionsAvailable_one": "خيار واحد متوفر",
  "picker.emptyTitle": "ما في خدمات",
  "picker.emptyBody": "جرّبوا كلمة ثانية أو اختاروا فئة ثانية.",
  // shared controls
  "common.save": "حفظ",
  "common.cancel": "إلغاء",
  "common.edit": "تعديل",
  "common.delete": "حذف",
  "common.print": "طباعة",
  "common.add": "إضافة",
  "common.none": "ما في",

  // the record of the relationship
  "nav.relationship": "سجل العلاقة",
  "nav.relationshipEyebrow": "الاجتماعات والقرارات وجهات التواصل والشروط",
  "nav.review": "المراجعة الربعية",
  "nav.reviewEyebrow": "الربع كامل في مكان واحد",
  "relationship.eyebrow": "كل اللي وراء الأرقام",
  "relationship.title": "سجل هذا العقد",
  "relationship.intro": "الاجتماعات اللي صارت معكم، والقرارات اللي طلعت منها، ومنو نكلّم من الطرفين، والمستندات اللي وراء الأرقام.",

  // renewal
  "renewal.title": "وين واصل العقد",
  "renewal.term": "مدة العقد",
  "renewal.elapsed": "مضى {n} شهر",
  "renewal.elapsed_one": "مضى شهر واحد",
  "renewal.remaining": "باقي {n} شهر",
  "renewal.remaining_one": "باقي شهر واحد",
  "renewal.daysLeft": "باقي {n} يوم",
  "renewal.daysLeft_one": "باقي يوم واحد",
  "renewal.running": "ماشي صح",
  "renewal.conversationBy": "افتحوا نقاش التجديد قبل {date}",
  "renewal.conversationDue": "نقاش التجديد استحق الحين",
  "renewal.expiring": "العقد ينتهي بعد {n} يوم",
  "renewal.expiring_one": "العقد ينتهي باچر",
  "renewal.expired": "العقد انتهى في {date}",

  // contacts
  "contacts.title": "جهات التواصل",
  "contacts.intro": "منو يعتمد من جهتهم، ومنو مسؤول من جهتنا.",
  "contacts.theirSide": "من جهتهم",
  "contacts.ourSide": "بريزمك",
  "contacts.approver": "يعتمد الشغل",
  "contacts.add": "إضافة جهة تواصل",
  "contacts.emptyTitle": "ما في جهات تواصل مسجلة",
  "contacts.emptyBody": "بريزمك تضيف الأشخاص اللي يعتمدون ويتابعون هذا الحساب.",

  // meetings
  "meetings.title": "محاضر الاجتماعات",
  "meetings.intro": "محاضر الاجتماعات اللي صارت معكم — مو ملاحظات داخلية.",
  "meetings.add": "تسجيل اجتماع",
  "meetings.attendees": "منو حضر",
  "meetings.summary": "شنو تم الاتفاق عليه",
  "meetings.heldOn": "تاريخ الاجتماع",
  "meetings.emptyTitle": "ما في اجتماعات مسجلة للحين",
  "meetings.emptyBody": "الاجتماعات اللي تصير معكم تطلع هني، الأحدث أول.",
  "meetings.recordedBy": "سجّلها {name}",

  // decisions
  "decisions.title": "القرارات",
  "decisions.intro": "ليش انصرف الكريدت، جنب السجل اللي يقول كم.",
  "decisions.add": "تسجيل قرار",
  "decisions.decidedOn": "تاريخ القرار",
  "decisions.rationale": "السبب",
  "decisions.aboutRequest": "يخص",
  "decisions.fromMeeting": "من اجتماع",
  "decisions.noLink": "مو مربوط بطلب",
  "decisions.emptyTitle": "ما في قرارات مسجلة للحين",
  "decisions.emptyBody": "السبب وراء الصرف يطلع هني.",

  // contract documents
  "documents.title": "مستندات العقد",
  "documents.intro": "عقد الريتينر الموقّع وقائمة الأسعار اللي وراء الأرقام.",
  "documents.add": "إضافة مستند",
  "documents.emptyTitle": "ما في مستندات للحين",
  "documents.emptyBody": "عقد الريتينر الموقّع وقائمة الأسعار ينحفظون هني.",
  "documents.addedBy": "أضافها {name}",

  // quarterly review
  "review.eyebrow": "الربع كامل في مكان واحد",
  "review.title": "المراجعة الربعية",
  "review.intro": "الثلاثة أشهر، والشغل اللي انسلّم، والنقاشات اللي شكّلته — مجمّعة من السجل نفسه، مو معادة الحساب.",
  "review.quarter": "الربع",
  "review.granted": "الكريدت الممنوح",
  "review.spent": "الكريدت المصروف",
  "review.committed": "الكريدت المحجوز",
  "review.closing": "الكريدت الباقي نهاية الربع",
  "review.byMonth": "شهر بشهر",
  "review.delivered": "شنو انسلّم",
  "review.deliveredCount": "{n} عمل",
  "review.deliveredCount_one": "عمل واحد",
  "review.byCategory": "وين راح الكريدت",
  "review.stillOpen": "لسّه مفتوح",
  "review.stillOpenBody": "انطلب في هذا الربع وما انسلّم للحين.",
  "review.nothingDelivered": "ما انسلّم شي في هذا الربع",
  "review.partialQuarter": "هذا الربع داخل العقد جزئياً بس. {months} برّا العقد وما تنحسب.",
  "review.outsideContract": "هذا الربع كامل برّا مدة العقد.",
  "review.noSpendCategories": "ما في صرف مكتمل في هذا الربع للحين",
  "review.carriedIn": "المرحّل للربع",
  "review.grantedNote": "المخصص الشهري والكريدت الإضافي. الكريدت المرحّل من الربع اللي قبل ينحسب مرة وحدة بس، على اليسار.",
  "review.unreconciled": "هالأرقام ما تطابق رصيد الإقفال في السجل وهو {closing} — الفرق {drift}. رقم الإقفال فوق هو رقم السجل نفسه، والتفاصيل معروضة عشان تشوفون وين الفرق.",
  "review.monthPool": "رصيد الشهر",
  "review.monthPoolNote": "رصيد الشهر هو مخصصه زائد اللي ترحّل له، عشان جذي مجموع الأشهر مو نفس الممنوح للربع.",
  "meetings.fieldTitle": "موضوع الاجتماع",
  "meetings.edit": "تعديل محضر الاجتماع",
  "decisions.fieldTitle": "شنو انقرر",
  "decisions.edit": "تعديل القرار",
  "contacts.edit": "تعديل جهة التواصل",
  "contacts.fieldSide": "أي طرف",
  "contacts.fieldName": "الاسم",
  "contacts.fieldRole": "المسمى الوظيفي",
  "contacts.fieldEmail": "الإيميل",
  "contacts.fieldPhone": "التلفون",
  "contacts.fieldNote": "ملاحظة",
  "contacts.notApprover": "ما يعتمد",
  "close.title": "إقفال الشهر",
  "close.intro": "الإقفال يثبّت اللي بيترحّل للشهر الجاي. وما ينضاف شي جديد على شهر مقفل.",
  "close.willCarry": "يترحّل للشهر الجاي",
  "close.pool": "رصيد هذا الشهر",
  "close.spent": "المصروف",
  "close.committed": "لسّه محجوز",
  "close.action": "إقفال {month}",
  "close.reopen": "إعادة فتح {month}",
  "close.closedBadge": "مقفل",
  "close.closedOn": "أقفله {name} في {date}",
  "close.carried": "{n} كريدت ترحّلت للشهر الجاي، معتمدة عند الإقفال.",
  "close.provisional": "هالرقم يتغيّر لين ينقفل الشهر.",
  "close.movingWork": "الإقفال ينقل هالشغل لـ {to}. ما انسلّم، وما يقدر يضل محسوب على شهر مقفل. وسعره ما يتغيّر:",
  "close.blockedNowhere": "هذا آخر شهر بالعقد وباقي شغل محسوب عليه. ما في شهر جاي ننقله له — سلّموه أو ألغوه أول.",
  "close.blockedNextClosed": "{month} مقفل، فالشغل الباقي ما عنده وين يروح. افتحوا ذاك الشهر أول.",
  "close.confirmMoving": "وينقل {n} طلب باقي لـ {to}.",
  "close.reopenSuperadmin": "بس السوبر أدمن يقدر يفتح شهر مقفل.",
  "close.blockedPrevious": "أقفلوا الشهر اللي قبل أول. الإقفال بغير ترتيب يثبّت رقم لسّه يتحرك تحته.",
  "close.reopenBlocked": "ما ينفتح — {month} مقفل. افتحوا ذاك أول.",
  "close.noteLabel": "ملاحظة للسجل",
  "close.confirm": "تأكيد الإقفال",
  "close.confirmBody": "هذا يرحّل {n} كريدت للشهر الجاي ويمنع أي شي جديد ينحسب على {month}. المدير يقدر يفتحه مرة ثانية.",
  "work.finalVersion": "النهائي",
  "work.filesInVersion": "{n} ملفات",
  "work.filesInVersion_one": "ملف واحد",
  "share.title": "شارك هذا الشغل",
  "share.intro": "رابط للقراءة بس، لناس برّا فريقكم. ما فيه كريدت ولا سجل — الشغل بس.",
  "share.create": "إنشاء رابط",
  "share.created": "انسوّى الرابط",
  "share.revoked": "انلغى الرابط",
  "share.revoke": "إلغاء",
  "share.copy": "نسخ",
  "share.copied": "اننسخ",
  "share.copyFailed": "ما اننسخ — حدّدوا الرابط وانسخوه يدوي.",
  "share.expires": "يوقف في {date}",
  "share.opened": "انفتح {n} مرات",
  "share.opened_one": "انفتح مرة وحدة",
  "share.notOpened": "ما انفتح للحين",
  "monthReview.eyebrow": "شهركم، مجمّع",
  "monthReview.title": "مراجعة {month}",
  "monthReview.intro": "كل اللي سوّيناه لكم هالشهر، وشنو استهلك، وشنو بيترحّل.",
  "monthReview.running": "{month} لسّه ماشي. هالأرقام بتتغيّر لين ينتهي.",
  "monthReview.closedNote": "هذا الشهر مقفل. الأرقام تحت نهائية.",
  "monthReview.delivered": "أعمال مسلّمة",
  "monthReview.used": "كريدت مستهلك",
  "monthReview.carried": "يترحّل للجاي",
  "monthReview.theWork": "شنو سوّينا",
  "monthReview.nothingYet": "ما انسلّم شي هالشهر للحين",
  "monthReview.nothingYetBody": "الشغل يطلع هني أول ما ينسلّم.",
  "monthReview.effort": "وين راح الشغل",
  "monthReview.talked": "شنو تكلّمنا عنه",
  "monthReview.nothingTalked": "ما في اجتماعات ولا قرارات مسجلة هالشهر",
  "monthReview.running_work": "يكمّل للشهر الجاي",
  "monthReview.runningBody": "انطلب هالشهر وما انسلّم للحين.",
  "monthReview.seeStatement": "شوفوا كشف الحساب كامل",
  "monthReview.outside": "هذا الشهر برّا مدة العقد.",
  "nav.monthReview": "مراجعة الشهر",
  "nav.monthReviewEyebrow": "شهركم، مجمّع",
  // Table headers. Defined ONCE because the same columns appear on the overview,
  // requests, the statement and the ledger.
  "table.request": "الطلب",
  "table.source": "المصدر",
  "table.credits": "الكريدت",
  "table.due": "الموعد",
  "table.status": "الحالة",
  "table.service": "الخدمة",
  "table.date": "التاريخ",
  "table.work": "الشغل",
  "table.value": "القيمة",
  "table.category": "الفئة",
  "table.share": "النسبة",
  "table.movement": "الحركة",
  "table.transaction": "العملية",
  "table.recordedBy": "سجّلها",
  "table.requestedBy": "طلبها",
  "table.approvedBy": "اعتمدها",
  "table.requested": "تاريخ الطلب",
  "table.quantity": "العدد",
  "table.language": "اللغة",
  "table.view": "عرض",
  "table.action": "إجراء",

  // How a request reached us.
  "source.portal": "المنصة",
  "source.whatsapp": "واتساب",
  "source.email": "إيميل",
  "source.meeting": "اجتماع",
  "source.phone": "تلفون",
  "source.workshop": "ورشة",
  "source.other": "غير ذلك",

  // overview — the rest of it
  "overview.requestsFor": "طلبات {month}",
  "overview.noSpendTitle": "ما في خصومات مكتملة للحين",
  "overview.noSpendBody": "الشغل المخلّص بيطلع هني حسب الفئة.",
  "overview.noRequestsTitle": "ما في طلبات هالشهر",
  "overview.noRequestsBody": "الطلبات الجديدة والتسليمات خارج المنصة بتنعرض هني.",
  "overview.createFirst": "سوّوا أول طلب",
  // requests — the list and the detail
  "requests.eyebrow2": "مسار الإنتاج",
  "requests.statusFilters": "فلاتر حالة الطلب",
  "requests.viewRequest": "عرض الطلب",
  "requests.emptyTitle": "ما في طلبات بهالعرض",
  "requests.emptyBody": "غيّروا فلتر الحالة أو سوّوا طلب جديد.",
  "requests.createRequest": "إنشاء طلب",
  "requests.preferredDue": "الموعد المفضّل",
  "requests.promisedBy": "بريزمك وعدت فيه",
  "requests.submittedBy": "قدّمه",
  "requests.actionedBy": "تصرّف فيه",
  "requests.created": "تاريخ الإنشاء",
  "requests.openReference": "فتح المرجع",
  "requests.history": "السجل",
  "requests.timelineFailed": "ما قدرنا نحمّل سجل هذا الطلب.",
  "requests.timelineEmpty": "ما في سجل للحين.",

  // the Prismic-side review panel
  "requestReview.title": "مراجعة بريزمك",
  "requestReview.intro": "الاعتماد يحجز مبلغ الكريدت النهائي. والإنجاز يقيّده في السجل.",
  "requests.openDeliveryFor": "فتح تسليم {title}",
  "review.newStatus": "الحالة الجديدة",
  "review.finalCredits": "الكريدت النهائي",
  "review.promisedToClient": "الموعد الموعود للعميل",
  "review.productionStage": "مرحلة الإنتاج",
  "review.note": "توضيح اختياري",


  // ledger
  "ledger.filters": "فلاتر السجل",
  "ledger.history": "سجل حركات الكريدت",
  "ledger.emptyTitle": "ما في حركات تطابق هذا الفلتر",
  "ledger.emptyBody": "اختاروا نوع عملية ثاني.",
  // shell and shared chrome
  "shell.productionCredits": "رصيد الإنتاج",
  "shell.updating": "قاعد يحدّث",
  "shell.mainNav": "التنقّل الرئيسي",
  "common.close": "إغلاق",
  "work.title2": "الشغل",

  // the brief form
  "form.essentialsIntro": "قولوا لنا شنو يعني نجاح لهالشغل",
  "form.quantityLabel": "عدد الطلب",
  "form.decrease": "تقليل العدد",
  "form.increase": "زيادة العدد",
  "form.dueDateLabel": "الموعد المفضّل",
  "form.channelLabel": "المنصة أو القناة",
  "form.arabic": "عربي",
  "form.english": "إنجليزي",

  // saved briefs
  "briefs.intro": "ابدوا من وحد كاتبينه من قبل",

  // the delivery hand-off page
  "delivery.unavailable": "هذا التسليم مو متوفر",
  "delivery.backToWork": "رجوع للشغل",
  "delivery.allWork": "كل الشغل المسلّم",
  "delivery.whatThisWas": "شنو كان هذا",
  "delivery.theBrief": "البريف",
  "delivery.howDidWeDo": "شلون كان شغلنا؟",
  "delivery.alsoFromTeam": "كمان من فريقكم",
  "briefs.intro2": "ابدوا من وحد كاتبينه من قبل",
  "statement.retainer": "عقد ريتينر مع بريزمك ميديا",
  "statement.unreconciled": "هذا الكشف ما يطابق رصيد الحساب.",
  "statement.summary": "الملخص",
  "statement.availableThisMonth": "الكريدت المتوفر هالشهر",
  "statement.billedThisMonth": "المسلّم والمحسوب هالشهر",
  "statement.heldNote": "محجوز من الرصيد لين يخلص الشغل",
  "statement.carryingForward": "يترحّل للشهر الجاي",
  "statement.detail": "التفاصيل",
  "statement.workBilled": "الشغل المحسوب هالشهر",
  "statement.inFlight": "قيد التنفيذ",
  "statement.breakdown": "التوزيع",
  "statement.whereWent": "وين راح الكريدت",
  "form.stepOf": "خطوة {n} من 2",
  "login.portalLabel": "منصة رصيد الإنتاج",
  "login.heroAlt": "شاشة استوديو بريزمك",
  "login.tagline1": "كل طلب.",
  "login.tagline2": "كل كريدت.",
  "login.tagline3": "بمكان واحد واضح.",
  "login.secureAccess": "دخول آمن للحساب",
  "login.signInTitle": "تسجيل الدخول للوحة",
  "login.email": "الإيميل",
  "login.password": "كلمة السر",
  "login.passwordPlaceholder": "اكتبوا كلمة السر",
  "login.localPreview": "معاينة محلية",
  "login.privateWorkspace": "مساحة عمل خاصة بالحساب",
  "password.title": "أمّنوا حسابكم",
  "password.eyebrow": "اختاروا كلمة سر خاصة فيكم",
  "password.current": "كلمة السر اللي دخلتوا فيها",
  "password.new": "كلمة السر الجديدة",
  "password.repeat": "أعيدوا كلمة السر الجديدة",
  "password.mustMatch": "لازم الاثنتين يكونون نفس الشي",
  "password.saving": "قاعد يحفظ",
  "password.save": "حفظ ومتابعة",
  "password.signOutInstead": "تسجيل خروج بدال ذلك",
  "consent.legend": "تنبيهات الإيميل",
  "consent.body": "نقدر نرسل لكم إيميل أول ما يوصل طلب، أو تتغيّر حالته، أو يخلص. بس هذا — ما في إعلانات. ما بنسألكم مرة ثانية.",
  "consent.yes": "إي، أبي إيميل عن طلباتي",
  "consent.no": "لا، بادخل المنصة بنفسي",
  "form.langBoth": "عربي وإنجليزي",
  "delivery.loading": "قاعد يحمّل التسليم",
  "catalogue.useStandard": "استخدام السعر القياسي",
  "catalogue.changeRate": "تغيير",
  "catalogue.setRate": "تحديد السعر",
  "share.gone": "هذا الرابط ما عاد يشتغل",
  "share.goneBody": "الروابط المشاركة تنتهي. اطلبوا رابط جديد من اللي أرسله لكم.",
  "share.open": "فتح",
  "share.stopsWorking": "هذا الرابط يوقف في {date}.",
  "status.all": "الكل",
  "requests.staffHeading": "راجعوا واحسبوا كل طلب",
  "requests.staffSub": "الاعتماد يحجز الكريدت، والإنجاز يقيّد الخصم النهائي في السجل.",
  "requests.clientSub": "الكريدت التقديري تأكده بريزمك عند الاعتماد، قبل ما يبدأ الإنتاج.",
  "error.loadDashboard": "ما قدرنا نحمّل اللوحة.",
  "workspace.opensTitle": "لوحتكم تفتح في {month}",
  "workspace.opensBody": "عقدكم يبدي فيه، والكريدت يبين من أول يوم.",
  "workspace.failedTitle": "ما قدرنا نفتح لوحتكم.",
  "workspace.failedBody": "الخلل من عندنا. جرّبوا مرة ثانية بعد شوي.",
  "workspace.retry": "جرّبوا مرة ثانية",
  "error.signIn": "ما قدرنا نسجّل دخولكم.",
  "error.openPreview": "ما قدرنا نفتح المعاينة.",
  "error.switchAccount": "ما قدرنا نبدّل الحساب.",
  "error.unreachable": "ما قدرنا نوصل للوحة.",
  "toast.switchedTo": "تشتغلون الحين في {name}",
  "toast.passwordUpdated": "انحدّثت كلمة السر",
  "delivery.headerTitle": "الشغل المسلّم",
  "delivery.headerEyebrow": "التسليم",
  "work.videoDelivered": "فيديو مسلّم",
  "work.documentDelivered": "مستند مسلّم",
  "work.fromClient": "من العميل",
  "work.deliveredByPrismic": "سلّمته بريزمك",
  "work.attachWhenReady": "أرفقوا الشغل النهائي أول ما يجهز",
  "work.prismicWillAdd": "بريزمك بتضيف الشغل النهائي هني",
  "error.loadFiles": "ما قدرنا نحمّل الملفات.",
  "error.upload": "ما قدرنا نرفع الملف.",
  "error.removeFile": "ما قدرنا نحذف الملف.",
  "error.saveTemplate": "ما قدرنا نحفظ القالب.",
  "error.submitRequest": "ما قدرنا نرسل الطلب.",
  "error.readMonth": "ما قدرنا نقرأ الشهر.",
  "error.closeMonth": "ما قدرنا نقفل الشهر.",
  "error.reopenMonth": "ما قدرنا نفتح الشهر.",
  "toast.fileAdded": "انضاف الملف",
  "form.includedService": "خدمة مشمولة",
  "form.rateEstimate": "تقدير حسب قائمة الأسعار",
  "form.noDeduction": "ما في خصم كريدت",
  "form.estimatedDeduction": "الخصم التقديري",
  "form.includedInHours": "مشمولة ضمن ساعات العمل",
  "form.submitRequest": "إرسال الطلب",
  "due.promised": "موعود",

  "form.savingBrief": "قاعد يحفظ…",
  "error.generic": "صار شي غلط.",
  "error.deleteBrief": "ما قدرنا نحذف هذا البريف.",
  "error.createLink": "ما قدرنا نسوّي الرابط.",
  "error.revokeLink": "ما قدرنا نلغي الرابط.",
  "error.saveRate": "ما قدرنا نحفظ السعر.",
  "error.changePassword": "ما قدرنا نغيّر كلمة السر.",
  "error.loadDelivery": "ما قدرنا نحمّل هذا التسليم.",
  "error.saveFeedback": "ما قدرنا نحفظ هذا.",
  "error.loadWork": "ما قدرنا نحمّل الشغل.",
  "error.buildReview": "ما قدرنا نجهّز المراجعة.",
  "error.copyRequest": "ما قدرنا ننسخ هذا الطلب.",
  "error.loadRecord": "ما قدرنا نحمّل السجل.",
  "error.removeContact": "ما قدرنا نحذف جهة التواصل.",
  "error.removeMeeting": "ما قدرنا نحذف المحضر.",
  "error.removeDecision": "ما قدرنا نحذف القرار.",
  "error.uploadDocument": "ما قدرنا نرفع المستند.",
  "error.removeDocument": "ما قدرنا نحذف المستند.",
  "error.saveMeeting": "ما قدرنا نحفظ المحضر.",
  "error.saveDecision": "ما قدرنا نحفظ القرار.",
  "error.saveContact": "ما قدرنا نحفظ جهة التواصل.",
  "error.updateRequest": "ما قدرنا نحدّث الطلب.",
  "toast.meetingUpdated": "انحدّث محضر الاجتماع",
  "toast.meetingRecorded": "انسجّل الاجتماع",
  "toast.decisionUpdated": "انحدّث القرار",
  "toast.decisionRecorded": "انسجّل القرار",
  "timeline.request_created": "انقدّم الطلب",
  "timeline.request_approved": "بريزمك اعتمدته",
  "timeline.request_in_progress": "بدأ الإنتاج",
  "timeline.request_completed": "انسلّم",
  "timeline.request_rejected": "انرفض",
  "timeline.request_canceled": "انلغى",
  "timeline.request_pending": "رجع للمراجعة",
  "timeline.work_delivered": "انرفعت الملفات",
  "timeline.brief_attached": "انرفق البريف",
  "timeline.attachment_removed": "انحذف ملف",
  "timeline.offline_delivery_logged": "بريزمك سجّلته",
  "timeline.feedback_given": "ملاحظات من العميل",
  "timeline.stage_changed": "تغيّرت المرحلة",
  "timeline.promised_date_set": "انحدد موعد التسليم",
  "timeline.billing_month_moved": "انتقل للشهر الجاي عند الإقفال",
  "timeline.work_shared": "انشارك برابط للقراءة بس",
  "timeline.share_revoked": "انلغى رابط المشاركة",
  "timeline.updated": "انحدّث",
  "stage.briefing": "البريف",
  "stage.scripting": "كتابة السكربت",
  "stage.production": "الإنتاج",
  "stage.editing": "المونتاج",
  "stage.review": "المراجعة",
  "stage.delivered": "التسليم",
  "kind.allocation": "المخصص الشهري",
  "kind.rollover": "المرحّل",
  "kind.topup": "كريدت إضافي",
  "kind.spend": "خصم خدمة",
  "kind.adjustment": "تعديل",
  "kind.refund": "استرجاع كريدت",
  "kind.all": "كل الحركات",
  "feedback.loved": "عجبنا وايد",
  "feedback.good": "زين",
  "feedback.changes": "يحتاج تعديل",
  "feedback.whatChanging": "شنو اللي يحتاج تعديل؟",
  "feedback.whatWorked": "شنو اللي عجبكم؟",
  "feedback.update": "تحديث",
  "feedback.someone": "أحدهم",
  "delivery.creditsUsed": "الكريدت المستهلك",
  "delivery.estimated": "تقديري",
  "login.signIn": "تسجيل الدخول",
  "requests.notCommitted": "ما انلتزم فيه للحين",
  "requests.deducted": "انخصم",
  "requests.managerApproval": "يحتاج اعتماد مدير",
  "requests.updateRequest": "تحديث الطلب",
  "form.startedFromBrief": "بديتوا من بريف محفوظ",
  "form.copiedFromRequest": "منسوخ من طلب سابق",
  "gallery.inProgress": "قاعد يتسوّى",
  "stage.aria": "مرحلة الإنتاج: {stage}",
  "form.prefillNote": "البريف والكمية معبّية. حطّوا تاريخ يديد، والكريدت المعروض هو سعر اليوم.",
  "col.movement": "الحركة",
  "col.credits": "الكريدت",
  "col.value": "القيمة",
  "col.date": "التاريخ",
  "col.approvedBy": "معتمد من",
  "col.requested": "تاريخ الطلب",
  "col.status": "الحالة",
  "col.due": "الاستحقاق",
  "col.category": "التصنيف",
  "col.share": "النسبة",
  "col.request": "الطلب",
  "requests.estimatedDeduction": "خصم تقديري",
  "common.send": "إرسال",
  "common.saving": "قاعد يحفظ…",
  "source.admin": "بريزمك",
  "source.system": "النظام",
  "common.exportCsv": "تصدير CSV",
  "deliverables.addToBrief": "أضف للبريف",
  "login.clientView": "دخول العميل",
  "login.prismicView": "دخول بريزمك",
  "requests.requestAgain": "اطلبها مرة ثانية",
  "ledger.note.allocation": "كريدت الإنتاج الشهري حسب العقد",
  "ledger.note.rollover": "كريدت ما انستخدم ومرحّل من الشهر اللي فات",
  "ledger.systemActor": "النظام",
  "ledger.entryCount": "{n} حركة",
  "ledger.entryCount_one": "حركة وحدة",
  "requests.platformChannel": "المنصة / القناة",
  "requests.notSpecified": "ما انحدد",
  "requests.notActioned": "ما أحد باشره للحين",
  "work.nothingDelivered": "ما انسلّم شي للحين",
  "delivery.inProduction": "قاعد يتسوّى — هذي الصفحة بتتعبى أول ما يخلص الشغل.",
  "login.intro": "شوفوا أرصدتكم، اطلبوا خدمات إنتاج، وتابعوا كل خصم معتمد.",
  "form.nothingSubmitted": "ابدوا الطلب تحت — ما انرسل شي للحين.",
  "review.noteLabel": "ملاحظة داخلية / للسجل",
  "statement.nothingDeducted": "الشغل معتمد بس ما انسلّم للحين، فما انخصم شي.",
  "statement.approvedNotDelivered": "معتمد وما انسلّم للحين",
  "role.manager": "مدير بريزمك",
  "role.account_manager": "مدير حساب بريزمك",
  "role.admin": "أدمن بريزمك",
  "role.superadmin": "أدمن عام بريزمك",
  "role.client": "العميل",
  "ledger.completedWork": "شغل مكتمل",
  "statement.inflow.rollover": "مرحّل من الشهر اللي فات",
  "statement.inflow.allocation": "المخصص الشهري حسب العقد",
  "statement.inflow.topup": "كريدت إضافي مشترى",
  "statement.inflow.refund": "كريدت مسترجع",
  "statement.inflow.adjustment": "تعديل يدوي",
  "statement.itemCount": "{n} بنود",
  "statement.itemCount_one": "بند واحد",
  "statement.creditsHeld": "{n} كريدت محجوز",
  "statement.footnote": "الكريدت يتحسب بـ {rate} دينار للوحدة، حسب العقد الممتد من {start} لين {end}. الكريدت اللي ما ينستخدم يترحّل. الشغل المعتمد ينحجز من الرصيد وما ينخصم إلا بعد التسليم.",
  "statement.driftBody": "رصيد الإقفال بالكشف {statementBalance} كريدت بس الحساب يقول {accountBalance} — الفرق {drift}. اعتمدوا رصيد الحساب هو الصح وبلّغوا بريزمك قبل ما تستخدمون هالكشف.",
  "statement.nothingBilled": "ما انحسب شي بـ{month}",
  "statement.fullBalanceCarries": "ما انخصم أي كريدت هالشهر. الرصيد كامل بيترحّل.",
  "shell.switchClient": "شغالين بـ{name}. بدّلوا العميل.",
  "shell.chooseClient": "اختاروا عميل",
  "shell.findClient": "دوّروا على عميل",
  "shell.noClientMatch": "ما في عميل بهذا الاسم",
  "overview.staffIntro": "{n} طلبات تنتظر المراجعة. الشغل اللي انسلّم خارج المنصة ينتسجل من Prismic operations.",
  "overview.staffIntro_one": "طلب واحد ينتظر المراجعة. الشغل اللي انسلّم خارج المنصة ينتسجل من Prismic operations.",
  "aria.removeFile": "احذفوا {name}",
  "aria.deleteBrief": "احذفوا البريف المحفوظ {name}",
  "aria.creditsFor": "كريدت {name}",
  "aria.openWork": "افتحوا {name}",
  "aria.viewRequest": "شوفوا {name}",
  "aria.poolTotal": "{credits} كريدت برصيد هذا الشهر",
  "toast.monthClosed": "{month} انقفل — {credits} كريدت انترحّل",
  "toast.requestSubmitted": "انرسل الطلب للمراجعة ({credits} كريدت).",
  "toast.requestMoved": "الطلب انتقل لـ{status}.",
  "toast.rateReset": "{name} رجع للسعر العادي",
  "toast.rateSet": "{name} انحدد بـ{credits} كريدت",
  "form.titlePlaceholder": "مثلاً {service} للعيد الوطني",
  "briefs.confirmDelete": "تبون تحذفون البريف المحفوظ \"{name}\"؟ الطلبات اللي انرفعت منه ما تتأثر.",
  "password.charsNeeded": "باقي {n} حروف",
  "password.charsNeeded_one": "باقي حرف واحد",
  "password.minLength": "12 حرف على الأقل",
} as const;
